// UTILS
import { nextTick } from "vue"
import { storeToRefs } from "pinia"
import { setup, createActor, fromPromise } from "xstate"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"

// TYPES
import type { MoveMachineEventType } from "./types"
import type { GlobalMachineEvent } from "@/machines/types"

// STORES
import { useAnswersStore } from "@/features/move/store/answers.store"
import { useQuestionsStore } from "@/features/move/store/questions.store"
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"

// MACHINE
import { globalActor } from "@/machines/globalMachine"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

// Loader for Move data
// to redirect to the right step after loading persisted progress
const loadMoveData = fromPromise(() =>
  withAssessmentLoadingMinDuration("MOVE", async () => {
    const i18nStore = useI18nStore()
    const { fetchMove, initMove, retakeTest } = useAnswersStore()
    const { fetchQuestions, fetchAudios } = useQuestionsStore()
    const { questionsLength } = storeToRefs(useQuestionsStore())
    const { assessmentPE } = storeToRefs(useUserStore())
    const { updateLocale, isCurrentLangFr } = i18nStore

    const move = await fetchMove()

    const currentMove = assessmentPE.value?.assessments?.move

    if (
      move.status === "todo" ||
      move.status === "inprogress" ||
      move.status === "finish"
    ) {
      const assessmentSummary = useUserStore().assessmentPE.assessments.move
      const isFinisedAndNotPassed =
        move?.status === "finish" && assessmentSummary?.not_passed

      // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
      let assessmentStatus = isFinisedAndNotPassed ? "todo" : move?.status

      // status can be "todo", "inprogress" or "finish"
      switch (assessmentStatus) {
        case "todo":
          if (isCurrentLangFr()) {
            await updateLocale(1) // Force FR
          } else {
            await updateLocale(3) // Force EN for other langs
          }

          await fetchQuestions()
          await fetchAudios()
          return { status: "new" }

        case "inprogress":
          await fetchQuestions()
          await fetchAudios()
          // If there is less answers than questions, we need to show the questions
          if (move.data.items.length < questionsLength.value) {
            return { status: "showQuestions" }
          }
          // If the user has complete every steps but status is inprogress, we need to send the results to backend
          else {
            return { status: "showLoadResults" }
          }

        case "finish":
          if (currentMove?.retake_test) {
            await retakeTest()
            await fetchQuestions()
            await fetchAudios()
            return { status: "new" }
          }
          return { status: "showFinal" }

        default:
          await fetchQuestions()
          await fetchAudios()
          return { status: "new" }
      }
    } else {
      await initMove()
      await fetchQuestions()
      await fetchAudios()
      return { status: "new" }
    }
  })
)

export const moveMachine = setup({
  actors: {
    loadMoveData,
  },
  types: {} as {
    events: MoveMachineEventType
  },
  actions: {},
}).createMachine({
  id: "moveMachine",
  initial: "loading",
  states: {
    loading: {
      // entry: [() => router.replace({ name: "moveLoading" })],
      entry: [
        () => {
          if (router.currentRoute.value.name !== "moveLoading") {
            router.replace({ name: "moveLoading" })
          }
        },
      ],
      invoke: {
        id: "loadMove",
        src: "loadMoveData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "tuto",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "moveTest" })],
      on: {
        done: { target: "test" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "moveTuto" })],
      on: {
        done: { target: "test" },
      },
    },
    test: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "moveTest") {
            router.replace({ name: "moveTest" })
          }
        },
      ],
      on: {
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "moveLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export let moveActor = createActor(moveMachine)

const moveActorObserver = {
  async complete() {
    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())

    const workflow_uuid = useCookies().get(COOKIES.WORKFLOW_UUID)
    await fetchNextAssessment(workflow_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)
      globalActor.send({ type: "GLOBAL.MOVE.DONE" })
    }
  },
}

moveActor.subscribe(moveActorObserver)

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const moveActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToMoveActor(observer: (_snapshot: unknown) => void) {
  moveActorObservers.push(observer)
  moveActor.subscribe(observer)
}

let hasMoveActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startMoveActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "move"

  if (hasMoveActorStarted && isEnteringAssessment) {
    moveActor.stop()
    moveActor = createActor(moveMachine)
    moveActor.subscribe(moveActorObserver)
    for (const observer of moveActorObservers) moveActor.subscribe(observer)
  }

  hasMoveActorStarted = true
  moveActor.start()
}
